import { useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import styled from 'styled-components';

import { Button } from 'shared/components/Button';
import { Modal } from 'shared/components/Modal';
import { PaymentVendor, PaymentVendorNameMap } from 'checkout/enums/PaymentVendor';
import { getRecommendedCurrency } from 'checkout/utils/getRecommendedCurrency';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { STORAGE } from 'shared/enums/SitePreferences';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { ITabName } from 'checkout/types/Tab';
import { t } from 'localization/utils/t';
import { getTranslations } from 'shared/utils/getTranslations';
import { useLocation } from 'shared/hooks/useLocation';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { useCurrency } from 'shared/hooks/useCurrency';

const TP = 'checkout.components.Payment.ChangeCurrencyModal';

interface IChangeCurrencyModal {
  onClose: () => void;
  selectedPaymentVendor: PaymentVendor;
  selectPaymentVendor?: (selectedPaymentVendor: PaymentVendor) => void;
}

export const ChangeCurrencyModal = ({
  onClose,
  selectedPaymentVendor,
  selectPaymentVendor,
}: IChangeCurrencyModal) => {
  const { changeTab, order, tab, updateOrder, availablePaymentMethods } = useCheckoutContext();
  const { selectedCountryCode } = useLocation();
  const { selectedCurrencyCode } = useCurrency();
  const [isLoading, setIsLoading] = useState(false);
  const [expectedCurrency, setExpectedCurrency] = useState('USD');
  const isPayPalCanadaEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PAYPAL_CANADA);
  const isPaymentAvailabilityServiceEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PAYMENT_AVAILABILITY_SERVICE,
  );
  const PaymentVendorNames = getTranslations(PaymentVendorNameMap);
  const recommendedCurrency = getRecommendedCurrency({
    userLocation: selectedCountryCode,
    userCurrency: selectedCurrencyCode,
    paymentVendor: selectedPaymentVendor,
    isOffer: !!order?.wantId,
    isPaymentAvailabilityServiceEnabled,
    availablePaymentMethods,
  });

  const handleChangeCurrency = async () => {
    setIsLoading(true);

    if (isPayPalCanadaEnabled) {
      Cookie.set(STORAGE.CURRENCY, recommendedCurrency);
    } else if (selectedPaymentVendor === PaymentVendor.Giropay) {
      Cookie.set(STORAGE.CURRENCY, 'EUR');
    } else {
      Cookie.set(STORAGE.CURRENCY, 'USD');
    }

    await updateOrder({ orderNumber: order.number, payload: { updateCurrency: true } });
    if (selectPaymentVendor) {
      selectPaymentVendor(selectedPaymentVendor);
    }
    if (tab === ITabName.REVIEW) {
      changeTab(ITabName.PAYMENT);
    }
    setIsLoading(false);
    onClose();
  };

  useEffect(() => {
    if (isPayPalCanadaEnabled) {
      setExpectedCurrency(recommendedCurrency);
    } else if (selectedPaymentVendor === PaymentVendor.Giropay) {
      setExpectedCurrency('EUR');
    } else {
      setExpectedCurrency('USD');
    }

    sendTrackingEvent('MODAL_OPEN_VIEW', {
      modal_name: 'update_currency',
    });
  }, []);

  return (
    <ChangeCurrencyModal.Modal
      data-qa="change_currency_modal"
      onClose={onClose}
      title={t(`${TP}.changeCurrencyTitle`, 'Update Currency')}
    >
      <ChangeCurrencyModal.Body>
        {t(
          `${TP}.changeCurrencyBody`,
          '{paymentVendor} is available in {currency} only. Would you like to update your currency preference to {currency}?',
          {
            paymentVendor: PaymentVendorNames[selectedPaymentVendor],
            currency: expectedCurrency,
          },
        )}
      </ChangeCurrencyModal.Body>
      <ChangeCurrencyModal.Cta>
        <Button
          data-qa="change_currency_confirm_button"
          onClick={handleChangeCurrency}
          loading={isLoading}
          disabled={isLoading}
        >
          {t(`${TP}.changeCurrencyOk`, 'UPDATE')}
        </Button>
      </ChangeCurrencyModal.Cta>
    </ChangeCurrencyModal.Modal>
  );
};

ChangeCurrencyModal.Modal = styled(Modal)`
  max-width: 335px;
`;

ChangeCurrencyModal.Body = styled.p`
  margin: 8px 0 32px;
`;

ChangeCurrencyModal.Cta = styled.div`
  display: flex;
`;
